import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';
import { useLandingTheme } from '../hooks/useLandingTheme';

interface AboutModalProps {
  open: boolean;
  onClose: () => void;
}

const AboutModal: React.FC<AboutModalProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const isDark = useLandingTheme();

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const title = t('landing.footer.about.title');
  const paragraphs = t('landing.footer.about.paragraphs', { returnObjects: true }) as string[];

  const modalBg = isDark ? '#0F172B' : '#FFFFFF';
  const modalBorder = isDark ? '#314158' : '#E2E8F0';
  const titleColor = isDark ? '#E2E8F0' : '#314158';
  const bodyColor = isDark ? '#CBD5E1' : '#334155';
  const mutedText = isDark ? '#90A1B9' : '#62748E';
  const sectionBorder = isDark ? '#1D293D' : '#F1F5F9';
  const headerBg = isDark ? '#1D293D' : '#F8FAFC';
  const iconContainerBg = isDark ? '#1C398E' : '#EFF6FF';
  const iconColor = isDark ? '#51A2FF' : '#2B7FFF';

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: isDark ? 'rgba(2, 6, 24, 0.7)' : 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '16px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        style={{
          background: modalBg,
          border: `1px solid ${modalBorder}`,
          borderRadius: '16px',
          width: '100%',
          maxWidth: '720px',
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: isDark
            ? '0 25px 50px rgba(2,6,24,0.5)'
            : '0 25px 50px rgba(0,0,0,0.15)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '16px 24px',
            borderBottom: `1px solid ${sectionBorder}`,
            background: headerBg,
            flexShrink: 0,
          }}
        >
          <div
            style={{
              width: '32px',
              height: '32px',
              borderRadius: '10px',
              background: iconContainerBg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Info size={18} style={{ color: iconColor }} />
          </div>
          <span
            className="font-geist"
            style={{ fontSize: '16px', fontWeight: 600, color: titleColor, flex: 1 }}
          >
            {title}
          </span>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '4px',
              color: mutedText,
              flexShrink: 0,
            }}
          >
            <X size={20} />
          </button>
        </div>

        <div
          className={isDark ? 'scrollbar-themed-dark' : 'scrollbar-themed-light'}
          style={{
            flex: 1,
            overflowY: 'auto',
            padding: '24px 28px 28px',
            minHeight: 0,
          }}
        >
          {Array.isArray(paragraphs) &&
            paragraphs.map((p, idx) => (
              <p
                key={idx}
                style={{
                  fontSize: '14px',
                  color: bodyColor,
                  lineHeight: '1.7',
                  marginBottom: idx === paragraphs.length - 1 ? 0 : '16px',
                }}
              >
                {p}
              </p>
            ))}
        </div>
      </div>
    </div>
  );
};

export default AboutModal;
